
import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
  Book as BookIcon,
  Code as CodeIcon,
  Terminal as TerminalIcon,
  Architecture as ArchitectureIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

// Types for documentation structure
interface DocSection {
  id: string;
  title: string;
  icon: JSX.Element;
  subsections: DocSubsection[];
}

interface DocSubsection {
  id: string;
  title: string;
  content: string;
}

const DocumentationHome: React.FC = () => {
  const { section = 'getting-started', subsection = 'introduction' } = useParams<{
    section?: string;
    subsection?: string;
  }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Documentation structure
  const docsData: DocSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <BookIcon />,
      subsections: [
        {
          id: 'introduction',
          title: 'Introduction',
          content: `
# Introduction to Loxia

Loxia is a multipurpose AI agent CLI tool designed to help developers and teams automate tasks, process data, and leverage AI capabilities directly from the command line.

## What is Loxia?

Loxia provides a command-line interface to access powerful AI capabilities including text analysis, document processing, data extraction, and more. It's built to be extensible, fast, and developer-friendly.

## Key Features

- Text analysis and natural language processing
- Document parsing and data extraction
- Image analysis and processing
- Custom workflow automation
- Easy integration with existing systems
- Secure API key management
- Pay-per-use pricing model

## Who is Loxia for?

Loxia is designed for developers, data scientists, and DevOps teams who need powerful AI capabilities in their workflow. Whether you're building applications, automating processes, or analyzing data, Loxia can help streamline your work.
          `,
        },
        {
          id: 'installation',
          title: 'Installation',
          content: `
# Installing Loxia

Loxia can be installed via npm, pip, or directly from a binary release.

## NPM Installation

\`\`\`bash
npm install -g loxia-cli
\`\`\`

## Python Installation

\`\`\`bash
pip install loxia
\`\`\`

## Binary Installation

Download the appropriate binary for your operating system from the [releases page](https://github.com/loxia/loxia-cli/releases).

### macOS/Linux

\`\`\`bash
# Download the binary
curl -LO https://github.com/loxia/loxia-cli/releases/latest/download/loxia-$(uname -s)-$(uname -m)

# Make it executable
chmod +x loxia-$(uname -s)-$(uname -m)

# Move to your PATH
sudo mv loxia-$(uname -s)-$(uname -m) /usr/local/bin/loxia
\`\`\`

### Windows

Download the Windows executable from the [releases page](https://github.com/loxia/loxia-cli/releases) and add it to your PATH.

## Verifying Installation

To verify that Loxia is installed correctly, run:

\`\`\`bash
loxia --version
\`\`\`

You should see the version number of Loxia displayed.
          `,
        },
        {
          id: 'authentication',
          title: 'Authentication',
          content: `
# Authentication

Before using Loxia, you need to authenticate with your API key.

## Getting an API Key

1. [Register](https://autopilot.loxia.ai/register) for a Loxia account
2. Navigate to the [API Keys](https://autopilot.loxia.ai/api-keys) section
3. Create a new API key

## Setting Up Authentication

You can authenticate with Loxia in several ways:

### CLI Authentication

\`\`\`bash
loxia auth --api-key YOUR_API_KEY
\`\`\`

This will store your API key securely in your config file.

### Environment Variable

You can set the \`LOXIA_API_KEY\` environment variable:

\`\`\`bash
export LOXIA_API_KEY=your_api_key
\`\`\`

### Configuration File

You can also manually edit the configuration file located at:

- Linux/macOS: \`~/.config/loxia/config.json\`
- Windows: \`%APPDATA%\\loxia\\config.json\`

Add your API key to the file:

\`\`\`json
{
  "api_key": "your_api_key"
}
\`\`\`

## Verifying Authentication

To verify that your authentication is working:

\`\`\`bash
loxia auth --verify
\`\`\`
          `,
        },
      ],
    },
    {
      id: 'commands',
      title: 'Command Reference',
      icon: <TerminalIcon />,
      subsections: [
        {
          id: 'basic-commands',
          title: 'Basic Commands',
          content: `
# Basic Commands

Loxia provides a variety of commands for different AI tasks. Here are the basic commands to get started.

## Text Analysis

### Analyze Text

\`\`\`bash
loxia analyze-text "Analyze the sentiment of this text"
\`\`\`

This will return a JSON object with sentiment analysis, entities, and other text properties.

### Extract Information

\`\`\`bash
loxia extract-info "Extract all dates and names from this text" --format json
\`\`\`

### Translate Text

\`\`\`bash
loxia translate "Hello world" --target-lang fr
\`\`\`

## Document Processing

### Process Document

\`\`\`bash
loxia process-document path/to/document.pdf
\`\`\`

### Extract Tables

\`\`\`bash
loxia extract-tables path/to/document.pdf --output tables.json
\`\`\`

## Image Analysis

### Analyze Image

\`\`\`bash
loxia analyze-image path/to/image.jpg
\`\`\`

### Extract Text from Image

\`\`\`bash
loxia extract-text-from-image path/to/image.png
\`\`\`

## Help and Documentation

To get help for any command:

\`\`\`bash
loxia [command] --help
\`\`\`

For general help:

\`\`\`bash
loxia --help
\`\`\`
          `,
        },
        {
          id: 'advanced-commands',
          title: 'Advanced Commands',
          content: `
# Advanced Commands

Loxia offers advanced commands for more complex AI tasks and automations.

## Workflow Automation

### Create Workflow

\`\`\`bash
loxia workflow create --name "my-workflow" --file workflow.yaml
\`\`\`

### Run Workflow

\`\`\`bash
loxia workflow run "my-workflow" --input data.json
\`\`\`

## Custom Models

### List Models

\`\`\`bash
loxia models list
\`\`\`

### Use Custom Model

\`\`\`bash
loxia analyze-text "Analyze this text" --model "custom-model-name"
\`\`\`

## Batch Processing

### Process Multiple Files

\`\`\`bash
loxia batch-process --input-dir ./documents --output-dir ./results
\`\`\`

## Data Transformation

### Transform Data

\`\`\`bash
loxia transform data.json --template template.json --output transformed.json
\`\`\`

## API Integration

### Generate API Code

\`\`\`bash
loxia generate-api-client --lang python --output ./client
\`\`\`

## Configuration Management

### View Configuration

\`\`\`bash
loxia config view
\`\`\`

### Set Configuration

\`\`\`bash
loxia config set timeout 30
\`\`\`
          `,
        },
      ],
    },
    {
      id: 'api-reference',
      title: 'API Reference',
      icon: <CodeIcon />,
      subsections: [
        {
          id: 'api-overview',
          title: 'API Overview',
          content: `
# API Overview

## Authentication

All API requests require authentication using an API key. Include your API key in the Authorization header:

\`\`\`
Authorization: Bearer YOUR_API_KEY
\`\`\`

## Response Format

All responses are returned in JSON format. Successful responses have a status code of 200 and follow this structure:

\`\`\`json
{
  "success": true,
  "data": {
    // Response data varies by endpoint
  }
}
\`\`\`

Error responses include an error message and appropriate HTTP status code:

\`\`\`json
{
  "success": false,
  "error": {
    "code": "error_code",
    "message": "Description of the error"
  }
}
\`\`\`

## Rate Limiting

The API is rate-limited to ensure fair usage. Rate limit information is included in the response headers:

- \`X-RateLimit-Limit\`: Number of requests allowed in the current period
- \`X-RateLimit-Remaining\`: Number of requests remaining in the current period
- \`X-RateLimit-Reset\`: Time (in Unix timestamp) when the rate limit will reset
          `,
        },
        {
          id: 'endpoints',
          title: 'API Endpoints',
          content: `
# API Endpoints

## Text Analysis

### Analyze Text

\`\`\`http
POST /text/analyze
\`\`\`

#### Request Body

\`\`\`json
{
  "text": "Text to analyze",
  "features": ["sentiment", "entities", "keywords"],
  "language": "en"
}
\`\`\`

#### Response

\`\`\`json
{
  "success": true,
  "data": {
    "sentiment": {
      "label": "positive",
      "score": 0.87
    },
    "entities": [
      {
        "type": "person",
        "text": "John Smith",
        "confidence": 0.95
      }
    ],
    "keywords": [
      {
        "text": "analyze",
        "relevance": 0.92
      }
    ]
  }
}
\`\`\`

### Extract Information

\`\`\`http
POST /text/extract
\`\`\`

#### Request Body

\`\`\`json
{
  "text": "Text to extract information from",
  "extractors": ["dates", "emails", "phones"]
}
\`\`\`

## Document Processing

### Process Document

\`\`\`http
POST /document/process
\`\`\`

#### Request Body

Use multipart/form-data to upload the document file.

## Image Analysis

### Analyze Image

\`\`\`http
POST /image/analyze
\`\`\`

#### Request Body

Use multipart/form-data to upload the image file.

\`\`\`json
{
  "features": ["objects", "faces", "labels"]
}
\`\`\`

## SDK Libraries

We provide SDK libraries for popular programming languages:

- [Python SDK](https://github.com/loxia/loxia-python)
- [JavaScript SDK](https://github.com/loxia/loxia-js)
- [Java SDK](https://github.com/loxia/loxia-java)
- [Go SDK](https://github.com/loxia/loxia-go)
          `,
        },
      ],
    },
    {
      id: 'examples',
      title: 'Examples & Guides',
      icon: <ArchitectureIcon />,
      subsections: [
        {
          id: 'basic-examples',
          title: 'Basic Examples',
          content: `
# Basic Usage Examples

Here are some basic examples to help you get started with Loxia.

## Text Analysis Example

This example shows how to analyze the sentiment of text and extract key entities.

\`\`\`bash
# Analyze text and output results as JSON
loxia analyze-text "I really enjoyed the conference yesterday in New York, especially the AI presentations." --output-format json
\`\`\`

Output:

\`\`\`json
{
  "sentiment": {
    "label": "positive",
    "score": 0.91
  },
  "entities": [
    {
      "type": "event",
      "text": "conference",
      "confidence": 0.85
    },
    {
      "type": "date",
      "text": "yesterday",
      "confidence": 0.92
    },
    {
      "type": "location",
      "text": "New York",
      "confidence": 0.98
    },
    {
      "type": "topic",
      "text": "AI presentations",
      "confidence": 0.87
    }
  ]
}
\`\`\`

## Document Processing Example

This example demonstrates how to extract information from a PDF document.

\`\`\`bash
# Extract all text from a PDF document
loxia extract-text document.pdf --output document-text.txt

# Extract tables from a PDF document
loxia extract-tables document.pdf --output tables.json
\`\`\`

## Scripting Example

This example shows how to use Loxia in a shell script for batch processing.

\`\`\`bash
#!/bin/bash
# Process all PDF documents in a directory

OUTPUT_DIR="./processed"
mkdir -p $OUTPUT_DIR

for file in *.pdf; do
  echo "Processing $file..."
  loxia process-document "$file" --output "$OUTPUT_DIR/\${file%.pdf}.json"
done

echo "All documents processed!"
\`\`\`
          `,
        },
        {
          id: 'integration-examples',
          title: 'Integration Examples',
          content: `
# Integration Examples

These examples demonstrate how to integrate Loxia with other tools and workflows.

## Python Integration

This example shows how to use Loxia from Python using the subprocess module.

\`\`\`python
import subprocess
import json

def analyze_text(text):
    # Call Autopilot One and capture the output
    result = subprocess.run(
        ["loxia", "analyze-text", text, "--output-format", "json"],
        capture_output=True,
        text=True,
        check=True
    )
    
    # Parse the JSON output
    return json.loads(result.stdout)

# Use the function
analysis = analyze_text("This product is amazing and very easy to use!")
print(f"Sentiment: {analysis['sentiment']['label']} (Score: {analysis['sentiment']['score']:.2f})")

# Extract entities
for entity in analysis.get('entities', []):
    print(f"Found {entity['type']}: {entity['text']}")
\`\`\`

## Node.js Integration

This example shows how to use Loxia from a Node.js application.

\`\`\`javascript
const { exec } = require('child_process');
const util = require('util');
const execPromise = util.promisify(exec);

async function processDocument(filePath) {
    try {
        const { stdout, stderr } = await execPromise(\`loxia process-document "\${filePath}" --output-format json\`);
        
        if (stderr) {
            console.error(\`Error: \${stderr}\`);
            return null;
        }
        
        return JSON.parse(stdout);
    } catch (error) {
        console.error(\`Execution error: \${error}\`);
        return null;
    }
}

// Use the function
processDocument('contract.pdf')
    .then(result => {
        if (result) {
            console.log('Document processed successfully:');
            console.log(\`Pages: \${result.pages.length}\`);
            console.log(\`Entities found: \${result.entities.length}\`);
        }
    });
\`\`\`

## CI/CD Integration

This example shows how to use Loxia in a GitHub Actions workflow for processing documentation.

\`\`\`yaml
name: Process Documentation

on:
  push:
    paths:
      - 'docs/**'

jobs:
  process-docs:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      
      - name: Install Loxia
        run: npm install -g loxia-cli
      
      - name: Configure Loxia
        run: loxia auth --api-key \${{ secrets.LOXIA_API_KEY }}
      
      - name: Process Documentation
        run: |
          mkdir -p processed
          for file in docs/*.md; do
            echo "Processing $file..."
            loxia extract-topics "$file" --output "processed/$(basename $file .md)-topics.json"
          done
      
      - name: Upload Results
        uses: actions/upload-artifact@v2
        with:
          name: processed-docs
          path: processed/
\`\`\`
          `,
        },
      ],
    },
    {
      id: 'settings',
      title: 'Configuration',
      icon: <SettingsIcon />,
      subsections: [
        {
          id: 'cli-settings',
          title: 'CLI Settings',
          content: `
# CLI Configuration

Loxia can be configured in various ways to customize its behavior.

## Configuration File

The Loxia configuration file is stored at:

- Linux/macOS: \`~/.config/loxia/config.json\`
- Windows: \`%APPDATA%\\loxia\\config.json\`

## Viewing Configuration

To view the current configuration:

\`\`\`bash
loxia config view
\`\`\`

## Setting Configuration Values

To set a configuration value:

\`\`\`bash
loxia config set <key> <value>
\`\`\`

For example:

\`\`\`bash
loxia config set timeout 60
\`\`\`

## Resetting Configuration

To reset a configuration value to its default:

\`\`\`bash
loxia config reset <key>
\`\`\`

To reset all configuration:

\`\`\`bash
loxia config reset --all
\`\`\`

## Available Configuration Options

| Option | Description | Default |
|--------|-------------|---------|
| \`timeout\` | Request timeout in seconds | 30 |
| \`output_format\` | Default output format (json, yaml, text) | json |
| \`log_level\` | Logging level (debug, info, warning, error) | info |
| \`cache_enabled\` | Enable response caching | true |
| \`cache_dir\` | Directory for caching responses | ~/.cache/loxia |
| \`cache_ttl\` | Cache time-to-live in seconds | 3600 |
| \`proxy\` | HTTP proxy URL | none |
| \`user_agent\` | Custom user agent string | Loxia-CLI/{version} |
| \`max_retries\` | Maximum number of retries for failed requests | 3 |
| \`retry_delay\` | Delay between retries in seconds | 1 |
| \`default_model\` | Default AI model to use | standard |

## Environment Variables

All configuration options can also be set using environment variables with the prefix \`LOXIA_\`:

\`\`\`bash
export LOXIA_TIMEOUT=60
export LOXIA_OUTPUT_FORMAT=yaml
export LOXIA_LOG_LEVEL=debug
\`\`\`

Environment variables take precedence over configuration file settings.
          `,
        },
        {
          id: 'advanced-settings',
          title: 'Advanced Settings',
          content: `
# Advanced Configuration

## Custom Plugin Directory

You can specify a custom directory for Loxia plugins:

\`\`\`bash
loxia config set plugin_dir ~/loxia-plugins
\`\`\`

## Network Settings

### Proxy Configuration

To use Loxia through a proxy:

\`\`\`bash
loxia config set proxy http://user:pass@proxy.example.com:8080
\`\`\`

### Custom Certificates

To use a custom CA certificate:

\`\`\`bash
loxia config set ca_cert_path /path/to/ca-cert.pem
\`\`\`

## Performance Tuning

### Concurrency

Set the maximum number of concurrent requests:

\`\`\`bash
loxia config set max_concurrency 5
\`\`\`

### Batch Size

Set the batch size for batch processing:

\`\`\`bash
loxia config set batch_size 50
\`\`\`

## Security Settings

### API Key Encryption

Enable encryption for stored API keys:

\`\`\`bash
loxia config set encrypt_keys true
\`\`\`

### Automatic Key Rotation

Configure automatic API key rotation:

\`\`\`bash
loxia config set key_rotation_days 90
\`\`\`

## Custom Templates

Specify a directory for custom output templates:

\`\`\`bash
loxia config set template_dir ~/loxia-templates
\`\`\`

## Debugging

Enable debug mode for verbose logging:

\`\`\`bash
loxia config set debug true
\`\`\`

## Configuration File Example

Here's a complete configuration file example:

\`\`\`json
{
  "api_key": "your_api_key",
  "timeout": 60,
  "output_format": "json",
  "log_level": "info",
  "cache_enabled": true,
  "cache_dir": "~/.cache/loxia",
  "cache_ttl": 3600,
  "proxy": null,
  "user_agent": null,
  "max_retries": 3,
  "retry_delay": 1,
  "default_model": "standard",
  "plugin_dir": "~/loxia-plugins",
  "max_concurrency": 5,
  "batch_size": 50,
  "encrypt_keys": false,
  "key_rotation_days": 90,
  "template_dir": "~/loxia-templates",
  "debug": false
}
\`\`\`
          `,
        },
      ],
    },
  ];

  // Find the current section and subsection
  const currentSection = docsData.find((s) => s.id === section) || docsData[0];
  const currentSubsection = currentSection.subsections.find((s) => s.id === subsection) || currentSection.subsections[0];

  // Toggle drawer for mobile view
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Sidebar content
  const drawerContent = (
    <Box>
      <List component="nav" aria-label="documentation sections">
        {docsData.map((docSection) => (
          <React.Fragment key={docSection.id}>
            <ListItemButton
              component={RouterLink}
              to={`/docs/${docSection.id}/${docSection.subsections[0].id}`}
              selected={currentSection.id === docSection.id}
            >
              <Box sx={{ mr: 2 }}>{docSection.icon}</Box>
              <ListItemText primary={docSection.title} />
            </ListItemButton>
            
            {currentSection.id === docSection.id && (
              <List component="div" disablePadding>
                {docSection.subsections.map((subsec) => (
                  <ListItemButton
                    key={subsec.id}
                    component={RouterLink}
                    to={`/docs/${docSection.id}/${subsec.id}`}
                    selected={currentSubsection.id === subsec.id}
                    sx={{ pl: 4 }}
                  >
                    <ChevronRightIcon fontSize="small" sx={{ mr: 1 }} />
                    <ListItemText primary={subsec.title} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '70vh' }}>
      {/* Mobile drawer toggle */}
      {isMobile && (
        <AppBar position="static" color="default" elevation={0} sx={{ mb: 2 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Documentation
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Grid container spacing={3}>
        {/* Sidebar for desktop */}
        {!isMobile && (
          <Grid item xs={12} md={3} lg={2.5}>
            <Paper elevation={1} sx={{ height: '100%' }}>
              {drawerContent}
            </Paper>
          </Grid>
        )}

        {/* Main content area */}
        <Grid item xs={12} md={9} lg={9.5}>
          <Paper elevation={1} sx={{ p: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
              <Link component={RouterLink} to="/docs" color="inherit">
                Documentation
              </Link>
              <Link
                component={RouterLink}
                to={`/docs/${currentSection.id}/${currentSection.subsections[0].id}`}
                color="inherit"
              >
                {currentSection.title}
              </Link>
              <Typography color="text.primary">{currentSubsection.title}</Typography>
            </Breadcrumbs>

            {/* Content */}
            <Box sx={{ 
              '& img': { maxWidth: '100%' },
              '& pre': { 
                backgroundColor: theme.palette.grey[100], 
                p: 2, 
                borderRadius: 1,
                overflow: 'auto'
              },
              '& code': {
                backgroundColor: theme.palette.grey[100],
                p: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace'
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 3,
                mb: 2
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                mb: 3
              },
              '& th, & td': {
                border: `1px solid ${theme.palette.divider}`,
                p: 1
              }
            }}>
              <ReactMarkdown>{currentSubsection.content}</ReactMarkdown>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentationHome;
